import * as THREE from 'three'
import { gpuCounts, type GpuCounts } from './dispose'
import type { ZoneFrame } from './zoneFrame'

/**
 * A mounted ground zone. Everything it allocates must be released by `dispose`,
 * because zones are entered and left many times in a session.
 */
export interface MountedZone {
  frame: ZoneFrame
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  /** Where the visitor is standing, for the exit handoff back to the map. */
  position: () => { lat: number; lon: number }
  /** What the camera is pointed at, so a photograph has a subject. */
  aim: () => ZoneAnchor | null
  /** Turn to look at a coordinate. Used by the scripted opening check. */
  face: (lat: number, lon: number) => void
  /**
   * Stand somewhere specific, facing a compass bearing. Art review needs fixed
   * vantage points: walking there depends on frame rate, and on a software
   * rasteriser the simulation clock crawls, so two runs never frame the same
   * shot.
   */
  warp: (lat: number, lon: number, bearingDeg?: number) => void
  /** `paused` holds the visitor still while something else owns the screen. */
  update: (dt: number, elapsed: number, paused: boolean) => void
  /** The zone owns its own post stack, so it can be cel-graded independently. */
  render: (dt: number) => void
  resize: (width: number, height: number) => void
  /** Nearest talkable NPC within range, if any. */
  nearbyNpc: () => { id: string; name: string; prompt: string; assignsQuestId?: string } | null
  dispose: () => void
}

export type ZoneId = 'la-fortuna'

/**
 * A content entry the visitor can walk up to. Discovery on foot has to feed the
 * same compendium as discovery from the air and discovery by GPS, or the zone
 * becomes a diorama with its own separate progress.
 */
export interface ZoneAnchor {
  id: string
  kind: 'attraction' | 'settlement'
  name: string
  lat: number
  lon: number
  radiusM?: number
}

export interface ZoneHostEvents {
  onEnter?: (zone: MountedZone) => void
  onExit?: (from: { lat: number; lon: number }) => void
  onStatus?: (message: string) => void
  /** Everything the world knows about; the zone keeps what falls inside it. */
  anchors?: () => ZoneAnchor[]
  onDiscover?: (anchor: ZoneAnchor) => void
  onArrive?: (zone: MountedZone) => void
}

export interface ZoneHost {
  enter: (id: ZoneId) => Promise<void>
  exit: () => void
  isActive: () => boolean
  current: () => MountedZone | null
  update: (dt: number, elapsed: number, paused?: boolean) => void
  resize: (width: number, height: number) => void
  render: (dt: number) => void
  aim: () => ZoneAnchor | null
  nearbyNpc: () => { id: string; name: string; prompt: string; assignsQuestId?: string } | null
  /** Baseline and current GPU counts, so leaks are observable rather than assumed. */
  audit: () => { baseline: GpuCounts; now: GpuCounts }
}

/**
 * Loaders are dynamic imports so no zone geometry, terrain, or town data lands
 * in the initial bundle. Someone who never walks into La Fortuna never pays for
 * it, which matters when the world chunk is already 604 KB gzipped.
 */
const LOADERS: Record<ZoneId, () => Promise<{ mountZone: MountZone }>> = {
  'la-fortuna': () => import('./laFortuna'),
}

export type MountZone = (context: {
  renderer: THREE.WebGLRenderer
  width: number
  height: number
  anchors: ZoneAnchor[]
  onDiscover: (anchor: ZoneAnchor) => void
}) => MountedZone

export function createZoneHost(
  renderer: THREE.WebGLRenderer,
  events: ZoneHostEvents = {},
): ZoneHost {
  const baseline = gpuCounts(renderer)
  let mounted: MountedZone | null = null
  let loading = false

  const enter = async (id: ZoneId) => {
    if (mounted || loading) return
    loading = true
    events.onStatus?.('Landing…')
    try {
      const { mountZone } = await LOADERS[id]()
      mounted = mountZone({
        renderer,
        width: window.innerWidth,
        height: window.innerHeight,
        anchors: events.anchors?.() ?? [],
        onDiscover: (anchor) => events.onDiscover?.(anchor),
      })
      events.onEnter?.(mounted)
      events.onArrive?.(mounted)
    } catch (error) {
      // The catch spans the callbacks as well as the mount, so a throw in a
      // greeting or a HUD update reads to the player as a failed landing. Say so
      // in development rather than letting it look like a missing tile.
      if (import.meta.env.DEV) console.error('zone entry failed', error)
      events.onStatus?.('Could not enter that place')
    } finally {
      loading = false
    }
  }

  const exit = () => {
    if (!mounted) return
    const from = mounted.position()
    mounted.dispose()
    mounted = null
    // Leaving frees geometry the region no longer references; without this the
    // next entry starts from a higher baseline every time.
    renderer.renderLists.dispose()
    events.onExit?.(from)
  }

  return {
    enter,
    exit,
    isActive: () => mounted !== null,
    current: () => mounted,
    update: (dt, elapsed, paused = false) => mounted?.update(dt, elapsed, paused),
    resize: (width, height) => mounted?.resize(width, height),
    render: (dt) => mounted?.render(dt),
    aim: () => mounted?.aim() ?? null,
    nearbyNpc: () => mounted?.nearbyNpc() ?? null,
    audit: () => ({ baseline, now: gpuCounts(renderer) }),
  }
}
